import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { StartupProfile } from '../types/firestore';
import {
  CATALOG_CATEGORY_LABELS,
  type CatalogCategory,
  isNearYouCatalogCategory,
  isSdgCatalogCategory,
  isSinceYouAddedFavoritesTitle,
  parseSdgNumberFromCatalogCategory,
} from '../constants/catalog-categories';
import { getSdgAlt, getSdgImage } from '../constants/sdgImages';
import locationIcon from '../assets/icons/location.png';
import eyeIcon from '../assets/eye.png';
import favoriteHeart from '../assets/favorites_icon__red_heart__right_red.png';
import StartupCard from './StartupCard';
import { resolveStartupId } from '../utils/startupId';
import InfiniteHorizontalScroll from './InfiniteHorizontalScroll';
import { FILTERED_GREEN_BUTTON } from '../constants/colors';

const SCROLL_FADE_PX_DESKTOP = 180;
const SCROLL_FADE_PX_MOBILE = 100; // Shorter distance on mobile so transition completes with less scroll

/** Matches SFY tab label row — all category headings use the same horizontal rhythm. */
const CATEGORY_HEADING_ROW =
  'mb-4 flex items-center gap-1.5 py-1 pl-2 pr-5 text-left text-lg !font-normal leading-tight text-white sm:gap-2 sm:pl-3 sm:pr-6 sm:text-2xl';

/** Same inset as CATEGORY_HEADING_ROW; used inside the favorites tab overlay. */
const SFY_TAB_LABEL_ROW =
  'absolute inset-0 flex items-center gap-1.5 py-1 pl-2 pr-5 transition-opacity duration-200 sm:gap-2 sm:pl-3 sm:pr-6';

/** Specially for you: a little space in front of the label, Rubik Medium. */
const SFY_TAB_LABEL_ROW_SPACED =
  'absolute inset-0 flex items-center gap-1.5 py-1 pl-3 pr-12 transition-opacity duration-200 sm:gap-2';

/** Aligns heading text & leading icons with the SFY ★ row. */
const CATEGORY_TITLE_NUDGE = '-translate-x-1 sm:-translate-x-2';
const TAB_LABEL_TYPE = { fontFamily: 'Rubik, sans-serif', fontWeight: 400 as const };
const SFY_LABEL_TYPE = { fontFamily: 'Rubik, sans-serif', fontWeight: 500 as const };
const CATEGORY_LEADING_ICON_NUDGE = 'inline-block shrink-0 -translate-x-1 sm:-translate-x-1.5';

/** Fits the label, a little space in front, and the slant. */
const SFY_TAB_W_PX = 272;
const FAVORITES_TAB_W_PX = 240;

function categoryTabPath(width: number): string {
  const flatEnd = width - 40;
  return `M0.5 40 L0.5 12 A 12 12 0 0 1 12.5 0.5 L${flatEnd} 0.5 C${flatEnd + 6} 0.5 ${flatEnd + 10} 2 ${flatEnd + 14} 8 L${width - 12} 32 C${width - 8} 38 ${width - 4} 40 ${width} 40`;
}

interface StartupRowProps {
  title: CatalogCategory;
  startups: StartupProfile[];
  onSelectStartup: (startup: StartupProfile, category?: CatalogCategory) => void;
  onPrefetchStartup?: (startup: StartupProfile) => void;
  viewedIds?: Set<string>;
  /** Startup ids currently in the investor’s favorites — shows Favorite corner label */
  favoriteIds?: Set<string>;
  hasOpenProfile?: boolean;
  hidePreviouslyViewedBadge?: boolean;
  /** Heart-only corner badge on favorited cards (Your Favorites row) */
  favoriteBadgeIconOnly?: boolean;
  count?: number;
  /** Use the dashboard category tab + box (Specially for you chrome). */
  categoryBox?: boolean;
  /**
   * Dim “Specially for you” title until the row scrolls into view (Activity & Settings),
   * then fade to full white — same idea as the dashboard open-profile fade.
   */
  fadeTitleOnScroll?: boolean;
  /** Override the default empty Specially-for-you body (e.g. shared profile signup CTA). */
  emptyBody?: React.ReactNode;
}

const StartupRow: React.FC<StartupRowProps> = ({
  title,
  startups,
  onSelectStartup,
  onPrefetchStartup,
  viewedIds,
  favoriteIds,
  hasOpenProfile = false,
  hidePreviouslyViewedBadge = false,
  favoriteBadgeIconOnly = false,
  count,
  categoryBox = false,
  fadeTitleOnScroll = false,
  emptyBody,
}) => {
  const isSpeciallyForYou = title === CATALOG_CATEGORY_LABELS.speciallyForYou;
  const isYourFavorites = title === CATALOG_CATEGORY_LABELS.yourFavorites;
  const showCategoryBox = isSpeciallyForYou || (isYourFavorites && categoryBox);
  const tabWidth = isSpeciallyForYou ? SFY_TAB_W_PX : FAVORITES_TAB_W_PX;
  const isPreviouslyViewed = title === CATALOG_CATEGORY_LABELS.previouslyViewed;
  const isWomenLed = title === CATALOG_CATEGORY_LABELS.womenLed;
  const showSdgIcons = isSdgCatalogCategory(title);
  const isNearYou = isNearYouCatalogCategory(title);
  const sdgNumberForTitle = parseSdgNumberFromCatalogCategory(title);
  const sdgTitleIconSrc = sdgNumberForTitle != null ? getSdgImage(sdgNumberForTitle) : null;
  // Row keeps “Based on your Favorites”; “Since you follow …” is badge-only.
  const displayTitle = isSpeciallyForYou
    ? `★ ${CATALOG_CATEGORY_LABELS.speciallyForYou}`
    : isSinceYouAddedFavoritesTitle(title)
      ? CATALOG_CATEGORY_LABELS.basedOnYourFavorites
      : title;

  // "Specially for you" text starts dim and becomes full white as user scrolls onto it
  const rowRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [scrollFadePx, setScrollFadePx] = useState(SCROLL_FADE_PX_DESKTOP);
  const [viewportFadeOpacity, setViewportFadeOpacity] = useState(0.2);

  useEffect(() => {
    const updateScroll = () => setScrollY(window.scrollY);
    updateScroll();
    window.addEventListener('scroll', updateScroll, { passive: true });
    return () => window.removeEventListener('scroll', updateScroll);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const updateThreshold = () => {
      setScrollFadePx(mql.matches ? SCROLL_FADE_PX_MOBILE : SCROLL_FADE_PX_DESKTOP);
    };
    updateThreshold();
    mql.addEventListener('change', updateThreshold);
    return () => mql.removeEventListener('change', updateThreshold);
  }, []);

  useEffect(() => {
    if (!isSpeciallyForYou || !fadeTitleOnScroll) return;

    const update = () => {
      const el = rowRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // Off while below the fold; full white once the row sits mid-viewport.
      const fadeStart = vh * 0.92;
      const fadeEnd = vh * 0.5;
      const progress = Math.min(1, Math.max(0, (fadeStart - rect.top) / (fadeStart - fadeEnd)));
      setViewportFadeOpacity(0.2 + progress * 0.8);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [isSpeciallyForYou, fadeTitleOnScroll]);

  const t = Math.min(scrollY / scrollFadePx, 1);
  const speciallyForYouOpacity = fadeTitleOnScroll
    ? viewportFadeOpacity
    : hasOpenProfile
      ? 0.5 + t * 0.5
      : 1;

  // Keep "Specially for you" first even when empty — same tab design as when it has cards.
  if (startups.length === 0 && isSpeciallyForYou) {
    return (
      <div
        ref={rowRef}
        className="relative mb-8 pt-[40px] lg:pt-0"
        data-testid="specially-for-you-empty"
      >
        <div
          className="absolute top-0 left-0 z-[15] isolate h-[40px] lg:-top-[40px]"
          style={{ width: SFY_TAB_W_PX }}
          data-catalog-sfy-tab=""
        >
          <svg
            className="pointer-events-none absolute inset-0 size-full"
            viewBox={`0 0 ${SFY_TAB_W_PX} 40`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d={categoryTabPath(SFY_TAB_W_PX)}
              fill="#333333"
              stroke="#555555"
              strokeWidth="1"
              strokeLinejoin="round"
              strokeLinecap="square"
            />
          </svg>
          <div className={SFY_TAB_LABEL_ROW_SPACED}>
            <span
              className="font-medium text-lg leading-none sm:text-2xl"
              style={{ ...SFY_LABEL_TYPE, color: `rgba(255,255,255,${speciallyForYouOpacity})` }}
            >
              ★
            </span>
            <h2
              className="mb-0 whitespace-nowrap font-medium text-lg tracking-normal sm:text-2xl"
              style={{ ...SFY_LABEL_TYPE, color: `rgba(255,255,255,${speciallyForYouOpacity})` }}
            >
              Specially for you
            </h2>
          </div>
        </div>
        <div
          className="relative rounded-2xl pt-6 pb-6 sm:pt-8"
          style={{
            backgroundColor: '#333333',
            border: '1px solid #555555',
            borderTopLeftRadius: 0,
          }}
        >
          <div className="rounded-lg bg-transparent px-3 py-8 text-center sm:px-6">
            {emptyBody ?? (
              <>
                <p className="mb-3 text-xl font-semibold text-white sm:text-2xl">
                  Startups matching your preferences will appear here.
                </p>
                <p className="mb-5 text-base text-white sm:text-lg">
                  Want to see more startups now?
                </p>
                <Link
                  to="/settings"
                  state={{ panel: 'editDataProfile' }}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 text-base font-medium ${FILTERED_GREEN_BUTTON}`}
                >
                  Broaden your preferences
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show empty state for "Your favorites" when there are no favorites
  if (startups.length === 0 && isYourFavorites) {
    if (showCategoryBox) {
      return (
        <div className="relative mb-8 pt-[40px] lg:pt-0">
          <div
            className="absolute top-0 left-0 z-[15] isolate h-[40px] lg:-top-[40px]"
            style={{ width: FAVORITES_TAB_W_PX }}
          >
            <svg
              className="pointer-events-none absolute inset-0 size-full"
              viewBox={`0 0 ${FAVORITES_TAB_W_PX} 40`}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
            >
              <path
                d={categoryTabPath(FAVORITES_TAB_W_PX)}
                fill="#333333"
                stroke="#555555"
                strokeWidth="1"
                strokeLinejoin="round"
                strokeLinecap="square"
              />
            </svg>
            <div className={SFY_TAB_LABEL_ROW}>
              <img
                src={favoriteHeart}
                alt=""
                className={`${CATEGORY_LEADING_ICON_NUDGE} h-6 w-6 object-contain sm:h-7 sm:w-7`}
              />
              <h2
                className={`mb-0 whitespace-nowrap text-lg !font-normal tracking-normal text-white sm:text-2xl ${CATEGORY_TITLE_NUDGE}`}
                style={TAB_LABEL_TYPE}
              >
                {CATALOG_CATEGORY_LABELS.yourFavorites}
              </h2>
            </div>
          </div>
          <div
            className="relative rounded-2xl pt-6 pb-6 sm:pt-8"
            style={{
              backgroundColor: '#333333',
              border: '1px solid #555555',
              borderTopLeftRadius: 0,
            }}
          >
            <div className="rounded-lg bg-transparent px-3 py-8 text-center sm:px-6">
              <p className="text-gray-400">Startups will appear here after you follow them</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="mb-8">
        <h2 className={CATEGORY_HEADING_ROW} style={TAB_LABEL_TYPE}>
          <img
            src={favoriteHeart}
            alt=""
            className={`${CATEGORY_LEADING_ICON_NUDGE} h-6 w-6 object-contain`}
          />
          <span className={CATEGORY_TITLE_NUDGE}>{CATALOG_CATEGORY_LABELS.yourFavorites}</span>
        </h2>
        <div className="rounded-lg bg-dark-950 p-8 px-3 text-center sm:px-6">
          <p className="text-gray-400">Startups will appear here after you follow them</p>
        </div>
      </div>
    );
  }

  // Women-led is always present in the feed order (even if currently empty)
  if (startups.length === 0 && isWomenLed) {
    return (
      <div className="mb-8">
        <h2 className={CATEGORY_HEADING_ROW} style={TAB_LABEL_TYPE}>
          <span className={CATEGORY_TITLE_NUDGE}>{CATALOG_CATEGORY_LABELS.womenLed}</span>
        </h2>
        <div className="rounded-lg bg-dark-950 p-8 px-3 text-center sm:px-6">
          <p className="text-gray-400">No women-led startups to show yet.</p>
        </div>
      </div>
    );
  }

  // Return null for other empty categories
  if (startups.length === 0) return null;

  // Prepare startup cards as children for InfiniteHorizontalScroll
  const startupCards = startups.map(startup => {
    const startupId = resolveStartupId(startup);
    return (
      <StartupCard
        key={startupId ?? startup.companyName}
        startup={{
          id: startupId ?? '',
          companyName: startup.companyName,
          thumbnailUrl: startup.thumbnailUrl,
          country: startup.country,
          industry: startup.industry,
          industries: startup.industries,
          businessModel: startup.businessModel,
          stage: startup.stage,
          investment: startup.investment,
          currency: startup.currency,
          selectedSDGs: startup.selectedSDGs,
        }}
        isViewed={
          hidePreviouslyViewedBadge
            ? false
            : startupId
              ? (viewedIds?.has(startupId) ?? false)
              : false
        }
        isFavorited={startupId ? (favoriteIds?.has(startupId) ?? false) : false}
        favoriteBadgeIconOnly={favoriteBadgeIconOnly}
        showSdgIcons={showSdgIcons}
        onClick={() => onSelectStartup(startup, title)}
        onMouseEnter={() => onPrefetchStartup?.(startup)}
      />
    );
  });

  return (
    <div
      ref={rowRef}
      className={`${isYourFavorites ? 'mb-1' : 'mb-8'} relative ${showCategoryBox ? 'pt-[40px] lg:pt-0' : ''}`}
    >
      {showCategoryBox && (
        <div
          className="absolute top-0 left-0 z-[15] isolate h-[40px] lg:-top-[40px]"
          style={{ width: tabWidth }}
          data-catalog-sfy-tab={isSpeciallyForYou ? '' : undefined}
        >
          <svg
            className="pointer-events-none absolute inset-0 size-full"
            viewBox={`0 0 ${tabWidth} 40`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d={categoryTabPath(tabWidth)}
              fill="#333333"
              stroke="#555555"
              strokeWidth="1"
              strokeLinejoin="round"
              strokeLinecap="square"
            />
          </svg>
          <div className={isSpeciallyForYou ? SFY_TAB_LABEL_ROW_SPACED : SFY_TAB_LABEL_ROW}>
            {isYourFavorites ? (
              <>
                <img
                  src={favoriteHeart}
                  alt=""
                  className={`${CATEGORY_LEADING_ICON_NUDGE} h-6 w-6 object-contain sm:h-7 sm:w-7`}
                />
                <h2
                  className={`mb-0 whitespace-nowrap text-lg !font-normal tracking-normal text-white sm:text-2xl ${CATEGORY_TITLE_NUDGE}`}
                  style={TAB_LABEL_TYPE}
                >
                  {CATALOG_CATEGORY_LABELS.yourFavorites}
                </h2>
              </>
            ) : (
              <>
                <span
                  className="font-medium text-lg leading-none sm:text-2xl"
                  style={{
                    ...SFY_LABEL_TYPE,
                    color: `rgba(255,255,255,${speciallyForYouOpacity})`,
                  }}
                >
                  ★
                </span>
                <h2
                  className="mb-0 whitespace-nowrap font-medium text-lg tracking-normal sm:text-2xl"
                  style={{
                    ...SFY_LABEL_TYPE,
                    color: `rgba(255,255,255,${speciallyForYouOpacity})`,
                  }}
                >
                  Specially for you
                </h2>
              </>
            )}
          </div>
        </div>
      )}
      {!showCategoryBox && (
        <h2 className={CATEGORY_HEADING_ROW} style={TAB_LABEL_TYPE}>
          {isYourFavorites ? (
            <>
              <span className={`relative inline-flex shrink-0 ${CATEGORY_LEADING_ICON_NUDGE}`}>
                <img src={favoriteHeart} alt="" className="h-6 w-6 object-contain sm:h-8 sm:w-8" />
                {count !== undefined && (
                  <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center h-4 min-w-[1rem] px-1 rounded-full bg-black text-white text-[10px] font-semibold leading-none">
                    {count}
                  </span>
                )}
              </span>
              <span className={CATEGORY_TITLE_NUDGE}>{displayTitle}</span>
            </>
          ) : sdgTitleIconSrc ? (
            <>
              <img
                src={sdgTitleIconSrc}
                alt={sdgNumberForTitle != null ? getSdgAlt(sdgNumberForTitle) : ''}
                className={`${CATEGORY_LEADING_ICON_NUDGE} h-7 w-7 rounded-sm object-contain sm:h-8 sm:w-8`}
              />
              <span className={CATEGORY_TITLE_NUDGE}>{displayTitle}</span>
            </>
          ) : isNearYou ? (
            <>
              <img
                src={locationIcon}
                alt=""
                className={`${CATEGORY_LEADING_ICON_NUDGE} h-6 w-6 object-contain sm:h-7 sm:w-7`}
                aria-hidden
              />
              <span className={CATEGORY_TITLE_NUDGE}>{displayTitle}</span>
            </>
          ) : isPreviouslyViewed ? (
            <>
              <img
                src={eyeIcon}
                alt=""
                className={`${CATEGORY_LEADING_ICON_NUDGE} h-6 w-6 object-contain opacity-90 sm:h-7 sm:w-7`}
                aria-hidden
              />
              <span className={CATEGORY_TITLE_NUDGE}>{displayTitle}</span>
            </>
          ) : (
            <span className={CATEGORY_TITLE_NUDGE}>{displayTitle}</span>
          )}
        </h2>
      )}
      <div
        className={`relative ${showCategoryBox ? 'rounded-2xl pt-6 pb-6 sm:pt-8' : ''}`}
        style={
          showCategoryBox
            ? ({
                ['--sfy-tab-w' as string]: `${tabWidth}px`,
                backgroundColor: '#262626',
                borderTop: 'none',
                borderLeft: '1px solid #555555',
                borderRight: '1px solid #555555',
                borderBottom: '1px solid #555555',
                boxShadow: '0 8px 20px rgba(0,0,0,0.22)',
                borderTopLeftRadius: '0',
              } as React.CSSProperties)
            : undefined
        }
      >
        {showCategoryBox && (
          <>
            {/* Synthetic top stroke: flat segment + SVG arc only (no second border-right vs parent). */}
            <div
              aria-hidden
              className="pointer-events-none absolute left-[var(--sfy-tab-w)] top-0 z-[7] h-px bg-[#555555] right-[1rem]"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute top-0 right-0 z-[8] size-[17px]"
            >
              <svg
                className="block size-[17px]"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* rounded-2xl TR outer arc R=16; extend flat stroke left for pixel overlap with hairline */}
                <path
                  d="M -4 0.5 H 1 A 16 16 0 0 1 17 16.5"
                  stroke="#555555"
                  strokeWidth="1"
                  strokeLinecap="square"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </>
        )}
        <InfiniteHorizontalScroll
          contentKey={startups
            .map(s => resolveStartupId(s))
            .filter((id): id is string => !!id)
            .join('|')}
          itemClassName="w-[calc(75vw-27px)] lg:w-[calc(18.75vw-28.5px)]"
          showArrows={true}
          loopWhenOverflow
        >
          {startupCards}
        </InfiniteHorizontalScroll>
      </div>
    </div>
  );
};

export default StartupRow;
